import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Calendar as BigCalendar,
  momentLocalizer,
  Views,
  View,
  SlotInfo,
  Components,
} from "react-big-calendar";
import moment from "moment";
import {
  Card,
  Form,
  Modal,
  notification,
  Tooltip,
  message,
  Col,
  Input,
  Button,
  Row,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  getEvents,
  deleteEvent,
  updateCalender,
  getCalender,
} from "../../utils/API";
import AddCreateeventmodal from "./Add";
import { EventWrapperProps } from "react-big-calendar";
// import socket from "../../utils/socket";
import globalreminder from "./GlobalReminderChecker";
import { AuthContext } from "../../Auth/AuthContext";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

const localizer = momentLocalizer(moment);

interface MyEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  description?: string;
  reminded?: boolean;
  reminderTime: number;
  link: string;
  userId: string;
}
interface CustomEventWrapperProps extends EventWrapperProps<MyEvent> {
  children?: React.ReactNode;
}

const GoogleLikeCalendar: React.FC = () => {
  const [form] = Form.useForm();
  const [events, setEvents] = useState<MyEvent[]>([]);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [view, setView] = useState<View>(Views.DAY);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<SlotInfo | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [calendarLink, setCalendarLink] = useState("");
  const { authData } = useContext(AuthContext);
  const userId = authData.user?._id;

  const handleSaveLink = async () => {
    try {
      if (!calendarLink) {
        message.warning("Please enter a calendar link");
        return;
      }

      const payload = {
        userId,
        link: calendarLink,
      };

      const res = await updateCalender(payload);

      // Assuming res.link returns the saved link
      if (res?.link) {
        setCalendarLink(res.link); // Update input with saved link
      }

      message.success("Calendar link saved!");
      console.log("Updated:", res);
    } catch (err) {
      message.error("Failed to save calendar link");
      console.error(err);
    }
  };

  const fetchCalendarLink = async () => {
    try {
      const res = await getCalender(); // res is an array
      if (res && res.length > 0 && res[0].link) {
        setCalendarLink(res[0].link); // set the input
        return res[0].link;
      } else {
        setCalendarLink(""); // no link found
        return null;
      }
    } catch (err) {
      console.error("Failed to fetch calendar link", err);
      setCalendarLink(""); // reset on error
      return null;
    }
  };

  useEffect(() => {
    const fetchCalendarLink = async () => {
      try {
        const res = await getCalender(); // res is an array
        if (res && res.length > 0 && res[0].link) {
          setCalendarLink(res[0].link); // set input value
        } else {
          setCalendarLink(""); // no link found
        }
      } catch (err) {
        console.error("Failed to fetch calendar link", err);
        setCalendarLink(""); // reset on error
      }
    };

    fetchCalendarLink();
  }, []);

  // Update your useEffect hooks
  // useEffect(() => {
  //   const onConnect = () => {
  //     setSocketConnected(true);
  //     const userId = localStorage.getItem('userId');
  //     if (userId) {
  //       // Register specifically for calendar reminders
  //       socket.emit('register-user', userId);
  //       socket.emit('join', `user-${userId}`);
  //     }
  //   };

  //   const onDisconnect = () => {
  //     setSocketConnected(false);
  //   };

  //   socket.on('connect', onConnect);
  //   socket.on('disconnect', onDisconnect);
  //   socket.connect();

  //   return () => {
  //     socket.off('connect', onConnect);
  //     socket.off('disconnect', onDisconnect);
  //     socket.disconnect();
  //   };
  // }, []);

  // Specific calendar reminder handler
  // useEffect(() => {
  //   const handleCalendarReminder = (data: {
  //     eventId: string;
  //     title: string;
  //     start: string;
  //     message: string;
  //   }) => {
  //     notification.info({
  //       message: "Calendar Reminder",
  //       description: data.message,
  //       placement: "topRight",
  //       duration: 5,
  //     });

  //     setEvents(prev =>
  //       prev.map(e =>
  //         e.id === data.eventId ? { ...e, reminded: true } : e
  //       )
  //     );
  //   };

  //   // Listen specifically to calendar reminders
  //   socket.on('reminder', (data: { module: any; message: any; eventId: string; }) => {
  //     notification.info({
  //       message: `${data.module} Reminder`, // show which module
  //       description: data.message,
  //       placement: "topRight",
  //       duration: 5,
  //     });

  //     setEvents(prev =>
  //       prev.map(e =>
  //         e.id === data.eventId ? { ...e, reminded: true } : e
  //       )
  //     );
  //   });

  //   return () => {
  //     socket.off('calendar-reminder', handleCalendarReminder);
  //   };
  // }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const res: any[] = await getEvents();

      const parsed = res
        .filter((ev) => ev.reminderTime > 0) // Only calendar events
        .map((ev) => ({
          id: ev._id ?? ev.id,
          title: ev.title,
          start: new Date(ev.start),
          end: new Date(ev.end),
          description: ev.description,
          reminded: !!ev.reminded,
          reminderTime: ev.reminderTime || 0,
          userId: ev.userId, // make sure event has userId
          link: ev.link, // event's link
        })) as MyEvent[];

      setEvents(parsed);

      // Set calendar link only if the event belongs to logged-in user

      const userEvent = res.find((ev) => ev.userId === userId);

      if (userEvent && userEvent.link) {
        setCalendarLink(userEvent.link);
      } else {
        setCalendarLink(""); // fallback if no matching event
      }

      // Fallback check for calendar events only
      if (!socketConnected) {
        checkUpcomingCalendarEvents(parsed);
      }
    } catch (err) {
      message.error("Failed to fetch calendar events");
      console.error("Calendar events error:", err);
    }
  }, [socketConnected, userId]);

  // Update fallback check for calendar events only
  const checkUpcomingCalendarEvents = (eventsList: MyEvent[]) => {
    const now = new Date();
    const soon = new Date(now.getTime() + 15 * 60 * 1000); // 15 minutes window

    eventsList.forEach((event) => {
      if (
        !event.reminded &&
        event.start > now &&
        event.start <= soon &&
        event.reminderTime > 0
      ) {
        notification.info({
          message: "Upcoming Calendar Event",
          description: `"${event.title}" starts soon at ${moment(event.start).format("h:mm A")}`,
          placement: "topRight",
        });
        setEvents((prev) =>
          prev.map((e) => (e.id === event.id ? { ...e, reminded: true } : e)),
        );
      }
    });
  };

  useEffect(() => {
    fetchEvents();

    const interval = setInterval(() => {
      if (!socketConnected) {
        fetchEvents();
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(interval);
  }, [fetchEvents, socketConnected]);

  // ===== REGISTER EMAIL TEMPLATE COLUMNS =====
  useEffect(() => {
    const calendarColumns = [
      { title: "Title", dataIndex: "title", key: "title" },
      { title: "Start Date", dataIndex: "start", key: "start" },
      { title: "End Date", dataIndex: "end", key: "end" },
      { title: "Description", dataIndex: "description", key: "description" },
      {
        title: "Reminder Time",
        dataIndex: "reminderTime",
        key: "reminderTime",
      },
      { title: "Link", dataIndex: "link", key: "link" },
    ];
    autoRegisterCurrentPageColumns(calendarColumns);
  }, []);

  const monthMatrix = useMemo(() => {
    const startOfMonth = moment(currentDate).startOf("month");
    const startCalendar = startOfMonth.clone().startOf("week");
    const matrix: moment.Moment[][] = [];
    for (let w = 0; w < 6; w++) {
      const week: moment.Moment[] = [];
      for (let d = 0; d < 7; d++) {
        week.push(startCalendar.clone().add(w * 7 + d, "days"));
      }
      matrix.push(week);
    }
    return matrix;
  }, [currentDate]);

  const handlePrevMonth = () =>
    setCurrentDate((d) => moment(d).subtract(1, "month").toDate());
  const handleNextMonth = () =>
    setCurrentDate((d) => moment(d).add(1, "month").toDate());
  const handleToday = () => {
    setCurrentDate(new Date());
    setView(Views.MONTH);
  };

  const onNavigate = (date: Date) => {
    setCurrentDate(date);
  };

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    setSelectedSlot(slotInfo);
    setIsModalVisible(true);
  };

  const handleAddOk = async () => {
    setIsLoading(true);
    try {
      await fetchEvents();
      setIsModalVisible(false);
      form.resetFields();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteEvent(id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      message.success("Event deleted successfully");
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Failed to delete event");
    } finally {
      setDeleteId(null);
    }
  };

  const EventComponent: React.FC<{ event: MyEvent }> = ({ event }) => (
    <Tooltip
      title={
        <div>
          <strong>{event.title}</strong>
          <br />
          {event.reminderTime > 0
            ? `Reminder: ${event.reminderTime} minutes before${
                event.reminded ? " (triggered)" : " (pending)"
              }`
            : "No reminder set"}
          {event.description && (
            <>
              <br />
              Description: {event.description}
            </>
          )}
          {!socketConnected && (
            <div style={{ color: "orange", marginTop: 4 }}>
              (Realtime updates disconnected)
            </div>
          )}
        </div>
      }
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {event.reminderTime > 0 && (
          <span style={{ fontSize: 10 }}>{event.reminded ? "✅" : "⏰"}</span>
        )}
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
          {event.title}
        </span>
        <DeleteOutlined
          style={{
            fontSize: 12,
            color: "rgba(255,255,255,0.9)",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            setDeleteId(event.id);
          }}
        />
      </div>
    </Tooltip>
  );

  const EventWrapper: React.FC<CustomEventWrapperProps> = ({
    event,
    children,
  }) => (
    <div
      className={
        event.reminderTime > 0
          ? event.reminded
            ? "triggered-reminder"
            : "pending-reminder"
          : ""
      }
    >
      {children}
    </div>
  );
  const components: Components<MyEvent> = {
    event: EventComponent,
    eventWrapper: EventWrapper,
    month: {
      header: ({ label }) => (
        <div style={{ color: "#5f6368", fontWeight: 600, padding: "8px 12px" }}>
          {label}
        </div>
      ),
      dateHeader: ({ date }) => (
        <div style={{ padding: "4px" }}>{date.getDate()}</div>
      ),
    },
    week: {
      header: ({ label }) => (
        <div style={{ color: "#5f6368", fontWeight: 600, padding: "8px 12px" }}>
          {label}
        </div>
      ),
    },
    day: {
      header: ({ label }) => (
        <div style={{ color: "#5f6368", fontWeight: 600, padding: "8px 12px" }}>
          {label}
        </div>
      ),
    },
  };

  const eventStyleGetter = (event: MyEvent) => ({
    style: {
      backgroundColor: "#1b6cd6ff",
      borderRadius: 18,
      border: "none",
      color: "white",
      padding: "4px 8px",
      boxShadow: "0 1px 0 rgba(0,0,0,0.08)",
      fontSize: 13,
      lineHeight: "16px",
      borderLeft:
        event.reminderTime > 0
          ? event.reminded
            ? "4px solid #52c41a"
            : "4px solid #fadb14"
          : "none",
    },
  });

  return (
    <div>
      <Row
        className="m-2"
        align="middle"
        justify="space-between"
        gutter={[16, 16]}
      >
        {/* Left side: Heading */}
        <Col xs={24} md={12}>
          <h2 className="headername m-0">Calendar</h2>
        </Col>

        {/* Right side: Input + Button */}
        <Col xs={24} md={12}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center", // ensures vertical alignment
              gap: 8,
              width: "100%",
            }}
          >
            <Input
              className="Allinput"
              placeholder="Enter link"
              value={calendarLink}
              onChange={(e) => setCalendarLink(e.target.value)}
              size="middle" // match button height
              style={{ maxWidth: 300, height: 32 }} // explicitly match heights
            />
            <Button
              type="primary"
              onClick={handleSaveLink}
              className="allbuttons"
              style={{ height: 32 }} // match input height
            >
              Save
            </Button>
          </div>
        </Col>
      </Row>

      <Card style={{ borderRadius: 10, marginTop: 20 }}>
        <div
          className="scrollbar-hide custom-scroll-table"
          style={{ overflowX: "auto" }}
        >
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            {/* <aside
              style={{
                width: 260,
                background: "#fff",
                borderRadius: 12,
                padding: 14,
                boxShadow: "0 6px 18px rgba(16,24,40,0.06)",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  style={{
                    border: "none",
                    background: "#f1f3f4",
                    borderRadius: 6,
                    padding: "4px 8px",
                    cursor: "pointer",
                  }}
                >
                  ‹
                </button>
                <div style={{ fontWeight: 600 }}>{moment(currentDate).format("MMMM YYYY")}</div>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  style={{
                    border: "none",
                    background: "#f1f3f4",
                    borderRadius: 6,
                    padding: "4px 8px",
                    cursor: "pointer",
                  }}
                >
                  ›
                </button>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }} aria-hidden>
                <thead>
                  <tr>
                    {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                      <th
                        key={d}
                        style={{
                          textAlign: "center",
                          fontSize: 12,
                          color: "#6b7280",
                          paddingBottom: 6,
                        }}
                      >
                        {d}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthMatrix.map((week, wi) => (
                    <tr key={wi}>
                      {week.map((dayMoment, di) => {
                        const isToday = dayMoment.isSame(moment(), "day");
                        const inMonth = dayMoment.isSame(currentDate, "month");
                        const isSelected = dayMoment.isSame(selectedDate, "day");
                        let backgroundColor = "transparent";
                        let textColor = inMonth ? "#222" : "#bdbdbd";
                        let fontWeight = "normal";
                        if (isToday) {
                          backgroundColor = "#1a73e8";
                          textColor = "#fff";
                          fontWeight = "bold";
                        } else if (isSelected) {
                          backgroundColor = "#afaba5ff";
                          textColor = "#fff";
                          fontWeight = "bold";
                        }
                        return (
                          <td
                            key={di}
                            onClick={() => {
                              setSelectedDate(dayMoment.clone());
                              setCurrentDate(dayMoment.toDate());
                              setView(Views.DAY);
                            }}
                            style={{
                              height: 34,
                              textAlign: "center",
                              borderRadius: 18,
                              padding: "4px",
                              fontSize: 13,
                              background: backgroundColor,
                              color: textColor,
                              cursor: inMonth ? "pointer" : "default",
                              fontWeight: fontWeight,
                            }}
                          >
                            {dayMoment.date()}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </aside> */}
            <main
              style={{
                flex: 1,
                minWidth: 0,
                background: "#fff",
                borderRadius: 12,
                padding: 12,
                boxShadow: "3px 1px 7px 12px rgba(16,24,40,0.06)",
              }}
            >
              {/* <BigCalendar
                localizer={localizer}
                events={events}
                date={currentDate}
                view={view}
                onNavigate={onNavigate}
                onView={(v) => setView(v)}
                defaultView={Views.DAY}
                views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
                selectable
                onSelectSlot={handleSelectSlot}
                startAccessor="start"
                endAccessor="end"
                components={components}
                eventPropGetter={eventStyleGetter}
                style={{
                  height: "60vh",
                  background: "#fff",
                  fontFamily: "inherit",
                }}
              /> */}
              {calendarLink ? (
                <iframe
                  src={calendarLink}
                  style={{ border: 0 }}
                  width="100%"
                  height="600"
                  frameBorder="0"
                  scrolling="no"
                />
              ) : (
                <div
                  style={{
                    height: 600,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "1px dashed #ccc",
                    borderRadius: 8,
                    color: "#888",
                    fontSize: 16,
                  }}
                >
                  Please add a calendar link
                </div>
              )}
            </main>
          </div>
          <Modal
            open={deleteId !== null}
            onOk={() => {
              if (deleteId) handleDelete(deleteId);
            }}
            onCancel={() => setDeleteId(null)}
            okText="Yes"
            cancelText="No"
            // footer={null}
            width={500}
            centered={false}
            destroyOnClose
            className="custom-modal"
            style={{
              bottom: 20,
              top: "10vw",
              maxHeight: "calc(100vh - 30px)",
              margin: "0 auto",
              position: "relative",
            }}
            title={
              <div
                style={{
                  marginLeft: "11px",
                  width: "100%",
                  paddingRight: 20,
                }}
              >
                Confirmation
              </div>
            }
            okButtonProps={{
              style: {
                backgroundColor: "#ff4d4f",
                borderColor: "#ff4d4f",
                color: "white",
              },
            }}
            bodyStyle={{
              padding: "22px",
              overflowY: "auto",
              fontFamily: "inter, sans-serif",
            }}
            styles={{
              header: {
                borderBottom: "1px solid #e0e0e0",
                padding: "12px",
                position: "sticky",
                top: 0,
                backgroundColor: "white",
              },
              content: {
                padding: 10,
                overflow: "hidden",
              },
              footer: {
                borderTop: "1px solid #e0e0e0",
                padding: "12px",
                position: "sticky",
                top: 0,
                backgroundColor: "white",
              },
            }}
          >
            <p>Are you sure you want to delete this Event?</p>
          </Modal>
          <AddCreateeventmodal
            visible={isModalVisible}
            loading={isLoading}
            form={form}
            onOk={handleAddOk}
            onCancel={() => setIsModalVisible(false)}
            onEventAdded={fetchEvents}
            selectedSlot={selectedSlot}
          />
        </div>
      </Card>
    </div>
  );
};

export const CALENDAR_TABLE_COLUMNS = [
  "title",
  "start",
  "end",
  "description",
  "reminderTime",
  "link",
];

export default GoogleLikeCalendar;
